import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete, Print } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import axios, { useApiRequest } from "../../../api/axios";
import { useCRUD } from "../../../api/crud";
import {
  EQUIPMENT_COUNTERS_BULK_ENDPOINT,
  EQUIPMENT_COUNTERS_ENDPOINT,
  EQUIPMENTS_ENDPOINT,
  ERROR_SNACKBAR,
  JOBS_ENDPOINT,
  SUCCESS_SNACKBAR,
} from "../../../consts";
import { formatCounterInputValue, formatCounterValue, normalizeCounterInputValue } from "../../../utils";
import { compareByCounterKind } from "../../../utils/counterKindOrder";
import { useHasPermission } from "../../../contexts/auth";
import AnimatedHelperText from "../animatedHelperText/animatedHelperText.component";
import CustomCircularProgress from "../customCircularProgress/customCircularProgress.component";
import CustomDateTimePicker from "../customDateTimePicker/customDateTimePicker.component";
import { DATE_TIME_PICKER_FORMAT } from "../datePickerFormats";
import styles from "../dynamicForm/dynamicForm.module.css";
import {
  CAN_ENTER_EVERY_COUNTER_VALUE_PERMISSION,
  LowCounterValueConfirmModal,
  LowCounterValueWarning,
  parseCounterNumericValue,
} from "./lowCounterValueWarning.component";

type HiddenCounterField = "equipment_id" | "job" | "kind" | "read_at" | "value";

type EquipmentOption = {
  id: number | string;
  full_name?: string;
  serial?: string;
  kind?: string;
};

type JobOption = {
  id: number | string;
  number?: string | null;
};

export type LastCounterData = {
  value: number;
  date: string;
  kind: string;
};

type EquipmentCounterApiRow = {
  value: number | null;
  read_at: string;
  kind: string;
};

type CounterFormData = Record<string, unknown>;

const COUNTER_KIND_CHOICES = [
  { value: "BP", label: "Black pages" },
  { value: "CP", label: "Color pages" },
  { value: "SP", label: "Scanned pages" },
] as const;

const BLACK_ONLY_EQUIPMENT_KIND = "B";

function getAvailableCounterKindChoices(equipmentKind?: string) {
  if (equipmentKind !== BLACK_ONLY_EQUIPMENT_KIND) {
    return COUNTER_KIND_CHOICES;
  }
  // Black-only equipment cannot produce color page counts.
  return COUNTER_KIND_CHOICES.filter((choice) => choice.value !== "CP");
}

type RawLowCounterValueWarning = {
  kind: string;
  enteredValue: number;
  latestValue: number;
  latestDate: string;
};

export interface EquipmentCounterFormProps {
  onSuccess?: (data?: any) => void;
  id?: string;
  equipmentId?: string | number | null;
  jobId?: string | number | null;
  initialData?: Record<string, unknown>;
  equipmentOptionsOverride?: EquipmentOption[];
  hiddenFields?: HiddenCounterField[];
  hideSubmit?: boolean;
  hidePreviousCountersTable?: boolean;
  hideLastValueHint?: boolean;
  wideFields?: boolean;
  onFormDataChange?: (data: Record<string, unknown>) => void;
}

const pruneEmptyStrings = (value: unknown): unknown => {
  if (value === null) return value;
  if (value === "") return undefined;

  if (Array.isArray(value)) {
    const cleanedArray = value
      .map((item) => pruneEmptyStrings(item))
      .filter((item) => item !== undefined);
    return cleanedArray.length > 0 ? cleanedArray : undefined;
  }

  if (value && typeof value === "object") {
    const cleanedObject = Object.entries(value as Record<string, unknown>).reduce(
      (acc, [key, entryValue]) => {
        const cleanedValue = pruneEmptyStrings(entryValue);
        if (cleanedValue !== undefined) {
          acc[key] = cleanedValue;
        }
        return acc;
      },
      {} as Record<string, unknown>
    );
    return Object.keys(cleanedObject).length > 0 ? cleanedObject : undefined;
  }

  return value;
};

const normalizeId = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const fieldBoxSx = {
  flex: { xs: "1 1 100%", sm: "1 1 calc((100% - 32px) / 3)" },
  maxWidth: { xs: "100%", sm: "calc((100% - 32px) / 3)" },
};

const wideFieldBoxSx = {
  flex: { xs: "1 1 100%", sm: "1 1 190px" },
  maxWidth: { xs: "100%", sm: "none" },
};

const getCounterKindLabel = (kind: string) =>
  COUNTER_KIND_CHOICES.find((choice) => choice.value === kind)?.label ?? kind;

const collectLowCounterValueWarnings = (
  entries: { formData: CounterFormData; lastCounters: LastCounterData[] }[]
): RawLowCounterValueWarning[] => {
  const warnings: RawLowCounterValueWarning[] = [];

  for (const { formData, lastCounters } of entries) {
    const kind = formData.kind;
    if (typeof kind !== "string" || !kind) {
      continue;
    }

    const numericValue = parseCounterNumericValue(formData.value);
    if (numericValue === null) {
      continue;
    }

    const latest = lastCounters.find((counter) => counter.kind === kind);
    if (!latest || numericValue >= latest.value) {
      continue;
    }

    warnings.push({
      kind,
      enteredValue: numericValue,
      latestValue: latest.value,
      latestDate: latest.date,
    });
  }

  return warnings;
};

const sortByCounterKind = (counters: LastCounterData[]) =>
  [...counters].sort((a, b) => compareByCounterKind(a.kind, b.kind));

export function useEquipmentLastCounters(equipmentId?: unknown): LastCounterData[] {
  const { executeRequest } = useApiRequest();
  const [lastCountersData, setLastCountersData] = useState<LastCounterData[]>([]);

  useEffect(() => {
    if (!equipmentId) {
      setLastCountersData([]);
      return;
    }

    let isCancelled = false;

    executeRequest(() =>
      axios.get(
        `${EQUIPMENT_COUNTERS_ENDPOINT}?equipment=${equipmentId}&ordering=-read_at&page_size=10`
      )
    ).then((result) => {
      if (isCancelled) return;

      const counters: EquipmentCounterApiRow[] = Array.isArray(result?.data?.results)
        ? result.data.results
        : [];
      const latestByKind = counters.reduce((acc: LastCounterData[], counter: EquipmentCounterApiRow) => {
        if (counter?.value == null || !counter?.kind) {
          return acc;
        }

        if (acc.some((item) => item.kind === counter.kind)) {
          return acc;
        }

        acc.push({
          kind: counter.kind,
          value: Number(counter.value),
          date: dayjs(counter.read_at).format(DATE_TIME_PICKER_FORMAT),
        });
        return acc;
      }, []);

      setLastCountersData(sortByCounterKind(latestByKind));
    });

    return () => {
      isCancelled = true;
    };
  }, [equipmentId, executeRequest]);

  return lastCountersData;
}

type EquipmentSummary = {
  kind?: string;
  fullName?: string;
  serial?: string;
};

// Resolves the equipment's own kind (Black-only vs Color) and display name
// so callers can figure out which counter kinds (BP/CP/SP) apply and show
// which equipment is being read, without needing it already loaded into an
// options list.
function useEquipmentSummary(equipmentId?: unknown): EquipmentSummary | undefined {
  const { executeRequest } = useApiRequest();
  const [summary, setSummary] = useState<EquipmentSummary | undefined>(undefined);

  useEffect(() => {
    if (!equipmentId) {
      setSummary(undefined);
      return;
    }

    let isCancelled = false;

    executeRequest(() => axios.get(`${EQUIPMENTS_ENDPOINT}${equipmentId}/`)).then((result) => {
      if (isCancelled) return;
      setSummary({
        kind: result?.data?.kind,
        fullName: result?.data?.full_name,
        serial: result?.data?.serial,
      });
    });

    return () => {
      isCancelled = true;
    };
  }, [equipmentId, executeRequest]);

  return summary;
}

const buildDefaultValues = ({
  equipmentId,
  jobId,
  initialData,
}: Pick<EquipmentCounterFormProps, "equipmentId" | "jobId" | "initialData">): CounterFormData => {
  const defaults: CounterFormData = { ...(initialData || {}) };

  if (equipmentId !== undefined) {
    defaults.equipment_id = equipmentId;
  }

  if (jobId !== undefined) {
    defaults.job = jobId;
  }

  if (
    defaults.provider === undefined
    || defaults.provider === null
    || defaults.provider === ""
  ) {
    defaults.provider = "U";
  }

  if (!defaults.read_at) {
    // Includes the local UTC offset (matches settlementCreator/
    // settlementCounterActions) - without it the backend has no way to know
    // which timezone this naive datetime string was written in and can
    // misinterpret it.
    defaults.read_at = dayjs().format("YYYY-MM-DDTHH:mm:ssZ");
  }

  return defaults;
};

const buildCounterPayload = (formData: CounterFormData): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    equipment_id: formData.equipment_id ?? null,
    job: formData.job ?? null,
    kind: formData.kind ?? "",
    provider: formData.provider ?? null,
    value: formData.value ?? null,
    read_at: formData.read_at ?? null,
  };

  return (pruneEmptyStrings(payload) ?? {}) as Record<string, unknown>;
};

const hasFilledCounterRow = (formData: CounterFormData): boolean =>
  formData.value !== null
  && formData.value !== undefined
  && formData.value !== ""
  && Boolean(formData.kind);

const getFieldErrorMessage = (fieldErrors: any, fieldName: string) => {
  const value = fieldErrors?.[fieldName];

  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }

  const firstError = value[0];
  if (typeof firstError === "string") {
    return firstError;
  }

  return firstError?.message ?? null;
};

const getRequestErrorMessage = (
  requestError: any,
  fallbackMessage: string
): string => {
  if (typeof requestError === "string") {
    return requestError;
  }

  if (typeof requestError?.message === "string") {
    return requestError.message;
  }

  if (typeof requestError?.detail === "string") {
    return requestError.detail;
  }

  return fallbackMessage;
};

interface EquipmentCounterRowFieldsProps {
  formData: CounterFormData;
  onChange: (data: CounterFormData) => void;
  hiddenFields: HiddenCounterField[];
  equipmentOptionsOverride?: EquipmentOption[];
  fieldErrors?: any;
  onLastCountersChange?: (data: LastCounterData[]) => void;
  hideLastValueHint?: boolean;
  wideFields?: boolean;
}

export function PreviousCountersTable({ equipmentId }: { equipmentId?: unknown }): ReactElement | null {
  const { t } = useTranslation();
  const lastCountersData = useEquipmentLastCounters(equipmentId);

  if (lastCountersData.length === 0) {
    return null;
  }

  return (
    <Box
      component="fieldset"
      sx={{
        width: "100%",
        boxSizing: "border-box",
        m: 0,
        px: 2,
        pb: 2,
        borderColor: "divider",
        borderRadius: 1,
        borderWidth: 1,
      }}
    >
      <Box
        component="legend"
        sx={{ px: 1, color: "text.secondary", fontSize: "0.875rem" }}
      >
        {t("Previous counter values")}
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("Kind")}</TableCell>
              <TableCell>{t("Value")}</TableCell>
              <TableCell>{t("Date")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lastCountersData.map((counter) => (
              <TableRow key={counter.kind}>
                <TableCell>{t(getCounterKindLabel(counter.kind))}</TableCell>
                <TableCell>{formatCounterValue(counter.value)}</TableCell>
                <TableCell>{counter.date}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function EquipmentCounterRowFields({
  formData,
  onChange,
  hiddenFields,
  equipmentOptionsOverride,
  fieldErrors,
  onLastCountersChange,
  hideLastValueHint = false,
  wideFields = false,
}: EquipmentCounterRowFieldsProps): ReactElement {
  const { t } = useTranslation();
  const { executeRequest } = useApiRequest();
  const [equipmentOptions, setEquipmentOptions] = useState<EquipmentOption[]>([]);
  const [jobOptions, setJobOptions] = useState<JobOption[]>([]);
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  const [jobLoading, setJobLoading] = useState(false);
  const lastCountersData = useEquipmentLastCounters(formData.equipment_id);

  useEffect(() => {
    onLastCountersChange?.(lastCountersData);
  }, [lastCountersData, onLastCountersChange]);

  const isHidden = useCallback(
    (fieldName: HiddenCounterField) => hiddenFields.includes(fieldName),
    [hiddenFields]
  );

  const setField = useCallback(
    (fieldName: string, value: unknown) => {
      onChange({ ...formData, [fieldName]: value });
    },
    [formData, onChange]
  );

  const fetchEquipmentOptions = useCallback(
    async (search?: string) => {
      if (equipmentOptionsOverride) return;

      setEquipmentLoading(true);
      try {
        const params = new URLSearchParams();
        if (search && search.trim().length >= 2) {
          params.set("search", search.trim());
        }

        const { data } = await executeRequest(() =>
          axios.get(
            `${EQUIPMENTS_ENDPOINT}${params.toString() ? `?${params.toString()}` : ""}`
          )
        );
        const nextOptions = Array.isArray(data?.results) ? data.results : data || [];
        setEquipmentOptions(nextOptions);
      } catch {
        setEquipmentOptions([]);
      } finally {
        setEquipmentLoading(false);
      }
    },
    [equipmentOptionsOverride, executeRequest]
  );

  const fetchJobOptions = useCallback(
    async (search?: string) => {
      setJobLoading(true);
      try {
        const params = new URLSearchParams();
        if (search && search.trim().length >= 2) {
          params.set("search", search.trim());
        }

        const { data } = await executeRequest(() =>
          axios.get(`${JOBS_ENDPOINT}${params.toString() ? `?${params.toString()}` : ""}`)
        );
        const nextOptions = Array.isArray(data?.results) ? data.results : data || [];
        setJobOptions(nextOptions);
      } catch {
        setJobOptions([]);
      } finally {
        setJobLoading(false);
      }
    },
    [executeRequest]
  );

  useEffect(() => {
    if (equipmentOptionsOverride || isHidden("equipment_id")) return;

    const selectedEquipmentId = formData.equipment_id;
    if (!selectedEquipmentId) return;

    const hasSelectedEquipment = equipmentOptions.some(
      (option) => normalizeId(option.id) === normalizeId(selectedEquipmentId)
    );
    if (hasSelectedEquipment) return;

    executeRequest(() => axios.get(`${EQUIPMENTS_ENDPOINT}${selectedEquipmentId}/`)).then((result) => {
      if (result?.data) {
        setEquipmentOptions((prev) => {
          if (prev.some((option) => normalizeId(option.id) === normalizeId(result.data.id))) {
            return prev;
          }
          return [...prev, result.data];
        });
      }
    });
  }, [equipmentOptions, equipmentOptionsOverride, executeRequest, formData.equipment_id, isHidden]);

  useEffect(() => {
    if (isHidden("job")) return;

    const selectedJobId = formData.job;
    if (!selectedJobId) return;

    const hasSelectedJob = jobOptions.some(
      (option) => normalizeId(option.id) === normalizeId(selectedJobId)
    );
    if (hasSelectedJob) return;

    executeRequest(() => axios.get(`${JOBS_ENDPOINT}${selectedJobId}/`)).then((result) => {
      if (result?.data) {
        setJobOptions((prev) => {
          if (prev.some((option) => normalizeId(option.id) === normalizeId(result.data.id))) {
            return prev;
          }
          return [...prev, result.data];
        });
      }
    });
  }, [executeRequest, formData.job, isHidden, jobOptions]);

  const currentEquipmentOptions = equipmentOptionsOverride ?? equipmentOptions;
  const selectedEquipment =
    currentEquipmentOptions.find(
      (option) => normalizeId(option.id) === normalizeId(formData.equipment_id)
    ) ?? null;
  const selectedJob =
    jobOptions.find((option) => normalizeId(option.id) === normalizeId(formData.job)) ?? null;
  const selectedLastCounter = lastCountersData.find(
    (counter) => counter.kind === formData.kind
  );
  const lastCounterHelperText = selectedLastCounter
    && `${t("last")}: ${formatCounterValue(selectedLastCounter.value)} (${selectedLastCounter.date})`;
  const availableKindChoices = getAvailableCounterKindChoices(selectedEquipment?.kind);

  useEffect(() => {
    if (isHidden("kind") || formData.kind) return;
    if (availableKindChoices.length === 1) {
      setField("kind", availableKindChoices[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableKindChoices, formData.kind, isHidden]);

  // Pre-fill the value field from the latest known counter reading for the
  // selected kind, so operators start from the last reading instead of a
  // blank field. Only applies while the value has never been touched at all
  // (undefined) - an existing counter being edited already has its own value
  // loaded, and a value the user deliberately cleared is "" rather than
  // undefined, so this must not re-fill it and fight the user's edit.
  useEffect(() => {
    if (formData.value !== undefined) return;
    if (!selectedLastCounter) return;
    setField("value", String(selectedLastCounter.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLastCounter, formData.value]);

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, width: "100%" }}>
      {!isHidden("equipment_id") && (
        <Box sx={wideFields ? wideFieldBoxSx : fieldBoxSx}>
          <Autocomplete
            size="small"
            options={currentEquipmentOptions}
            value={selectedEquipment}
            loading={!equipmentOptionsOverride && equipmentLoading}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : option.full_name || option.serial || String(option.id)
            }
            isOptionEqualToValue={(option, value) => normalizeId(option?.id) === normalizeId(value?.id)}
            onOpen={() => fetchEquipmentOptions()}
            onInputChange={(_, value, reason) => {
              if (equipmentOptionsOverride || reason !== "input") return;
              if (!value) {
                fetchEquipmentOptions();
                return;
              }
              fetchEquipmentOptions(value);
            }}
            onChange={(_, newValue) => setField("equipment_id", newValue?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Equipment")}
                required
                fullWidth
              />
            )}
            noOptionsText={t("No options")}
          />
          {getFieldErrorMessage(fieldErrors, "equipment_id") && (
            <div className={styles.errorMessage}>{getFieldErrorMessage(fieldErrors, "equipment_id")}</div>
          )}
        </Box>
      )}

      {!isHidden("kind") && (
        <Box sx={fieldBoxSx}>
          <FormControl fullWidth size="small" required>
            <InputLabel>{t("Kind")}</InputLabel>
            <Select
              label={t("Kind")}
              value={(formData.kind as string) ?? ""}
              onChange={(event) => setField("kind", event.target.value)}
            >
              {availableKindChoices.map((choice) => (
                <MenuItem key={choice.value} value={choice.value}>
                  {t(choice.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {getFieldErrorMessage(fieldErrors, "kind") && (
            <div className={styles.errorMessage}>{getFieldErrorMessage(fieldErrors, "kind")}</div>
          )}
        </Box>
      )}

      {!isHidden("value") && (
        <Box sx={wideFields ? wideFieldBoxSx : fieldBoxSx}>
          <TextField
            size="small"
            label={t("Value")}
            helperText={
              !hideLastValueHint && (
                <AnimatedHelperText visible={!!selectedLastCounter}>
                  {lastCounterHelperText}
                </AnimatedHelperText>
              )
            }
            value={
              formData.value === null || formData.value === undefined
                ? ""
                : formatCounterInputValue(String(formData.value))
            }
            required
            onChange={(event) => {
              const nextValue = event.target.value;

              if (nextValue === "") {
                // "" (not null/undefined) marks this as deliberately cleared
                // by the user, as opposed to never having been touched - see
                // the last-known-value pre-fill effect above.
                setField("value", "");
                return;
              }

              const normalizedValue = normalizeCounterInputValue(nextValue);
              if (normalizedValue === null) {
                return;
              }

              setField("value", normalizedValue);
            }}
            fullWidth
            type="text"
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          />
          {getFieldErrorMessage(fieldErrors, "value") && (
            <div className={styles.errorMessage}>{getFieldErrorMessage(fieldErrors, "value")}</div>
          )}
        </Box>
      )}

      {!isHidden("job") && (
        <Box sx={fieldBoxSx}>
          <Autocomplete
            size="small"
            options={jobOptions}
            value={selectedJob}
            loading={jobLoading}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : option.number || String(option.id)
            }
            isOptionEqualToValue={(option, value) => normalizeId(option?.id) === normalizeId(value?.id)}
            onOpen={() => fetchJobOptions()}
            onInputChange={(_, value, reason) => {
              if (reason !== "input") return;
              if (!value) {
                fetchJobOptions();
                return;
              }
              fetchJobOptions(value);
            }}
            onChange={(_, newValue) => setField("job", newValue?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Job")}
                fullWidth
              />
            )}
            noOptionsText={t("No options")}
          />
          {getFieldErrorMessage(fieldErrors, "job") && (
            <div className={styles.errorMessage}>{getFieldErrorMessage(fieldErrors, "job")}</div>
          )}
        </Box>
      )}

      {!isHidden("read_at") && (
        <Box sx={wideFields ? wideFieldBoxSx : fieldBoxSx}>
          <CustomDateTimePicker
            label={t("Read at")}
            value={formData.read_at ? dayjs(formData.read_at as string) : null}
            onChange={(value: Dayjs | null) =>
              setField("read_at", value ? value.format("YYYY-MM-DDTHH:mm:ssZ") : null)
            }
            ampm={false}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                required: true,
              },
            }}
          />
          {getFieldErrorMessage(fieldErrors, "read_at") && (
            <div className={styles.errorMessage}>{getFieldErrorMessage(fieldErrors, "read_at")}</div>
          )}
        </Box>
      )}
    </Box>
  );
}

export default function EquipmentCounterForm({
  onSuccess,
  id,
  equipmentId,
  jobId,
  initialData,
  equipmentOptionsOverride,
  hiddenFields = [],
  hideSubmit = false,
  hidePreviousCountersTable = false,
  hideLastValueHint = false,
  wideFields = false,
  onFormDataChange,
}: EquipmentCounterFormProps): ReactElement {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const canEnterEveryCounterValue = useHasPermission(CAN_ENTER_EVERY_COUNTER_VALUE_PERMISSION);
  const { get, create, update, loading, error } = useCRUD(EQUIPMENT_COUNTERS_ENDPOINT);
  const { executeRequest: executeBulkRequest, loading: bulkLoading } = useApiRequest();
  const [formData, setFormData] = useState<CounterFormData>({});
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [lowValueConfirmOpen, setLowValueConfirmOpen] = useState(false);
  const [lowValueWarnings, setLowValueWarnings] = useState<LowCounterValueWarning[]>([]);
  const initializedWithoutIdRef = useRef(false);
  const lastEmittedFormDataRef = useRef<string>("");
  const isMultiCreateMode = !id && !hideSubmit && !onFormDataChange;
  const singleModeLastCounters = useEquipmentLastCounters(formData.equipment_id);

  // Multi-create mode is a two-step flow: pick the equipment (skipped when
  // equipmentId is already known from context), then fill in a value for
  // every counter kind that equipment supports.
  const [equipmentSelectorData, setEquipmentSelectorData] = useState<CounterFormData>({});
  const [multiCreateEquipmentId, setMultiCreateEquipmentId] = useState<
    string | number | null
  >(equipmentId ?? null);
  const [kindValues, setKindValues] = useState<Record<string, string>>({});
  // Scanned pages is opt-in, added on demand via a button, rather than
  // always shown alongside the other counter kinds.
  const [showScanCounter, setShowScanCounter] = useState(false);
  const multiCreateEquipment = useEquipmentSummary(
    isMultiCreateMode ? multiCreateEquipmentId : undefined
  );
  const multiCreateLastCounters = useEquipmentLastCounters(
    isMultiCreateMode ? multiCreateEquipmentId : undefined
  );
  const availableKindChoices = useMemo(
    () => getAvailableCounterKindChoices(multiCreateEquipment?.kind),
    [multiCreateEquipment?.kind]
  );
  const primaryKindChoices = useMemo(
    () => availableKindChoices.filter((choice) => choice.value !== "SP"),
    [availableKindChoices]
  );
  const scanKindChoice = useMemo(
    () => availableKindChoices.find((choice) => choice.value === "SP"),
    [availableKindChoices]
  );
  const visibleKindChoices = useMemo(
    () =>
      scanKindChoice && showScanCounter ? [...primaryKindChoices, scanKindChoice] : primaryKindChoices,
    [primaryKindChoices, scanKindChoice, showScanCounter]
  );

  useEffect(() => {
    if (equipmentId != null) {
      setMultiCreateEquipmentId(equipmentId);
    }
  }, [equipmentId]);

  // Starting fresh on a new equipment - drop any values entered for the
  // previous one so they don't leak across selections.
  useEffect(() => {
    setKindValues({});
    setShowScanCounter(false);
  }, [multiCreateEquipmentId]);

  // Pre-fill each kind's value from the latest known counter reading, same
  // as the single-row form - but only until the user actually touches it.
  // Leave it untouched (rather than defaulting to "") when no match is
  // found yet, since the last-counters fetch is async and may simply not
  // have resolved on this pass - filling "" here would permanently mark
  // the kind as "already initialized" and block the real value once the
  // fetch does resolve.
  useEffect(() => {
    if (!isMultiCreateMode) return;

    setKindValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const choice of availableKindChoices) {
        if (next[choice.value] === undefined) {
          const last = multiCreateLastCounters.find((counter) => counter.kind === choice.value);
          if (!last) {
            continue;
          }
          next[choice.value] = String(last.value);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [availableKindChoices, multiCreateLastCounters, isMultiCreateMode]);

  const getDefaultValues = useCallback(
    () =>
      buildDefaultValues({
        equipmentId,
        jobId,
        initialData,
      }),
    [equipmentId, initialData, jobId]
  );

  useEffect(() => {
    if (isMultiCreateMode) {
      setIsLoadingInitial(false);
      return;
    }

    const defaultValues = getDefaultValues();

    if (!id) {
      if (!initializedWithoutIdRef.current) {
        setFormData(defaultValues);
        initializedWithoutIdRef.current = true;
      }
      setIsLoadingInitial(false);
      return;
    }

    get(id)
      .then((response) => {
        if (!response?.data) {
          setFormData(defaultValues);
          setIsLoadingInitial(false);
          return;
        }

        setFormData({
          ...defaultValues,
          ...response.data,
        });
        setIsLoadingInitial(false);
      })
      .catch(() => {
        setFormData(defaultValues);
        setIsLoadingInitial(false);
      });
  }, [get, getDefaultValues, id, isMultiCreateMode]);

  useEffect(() => {
    if (isMultiCreateMode) return;
    if (isLoadingInitial) return;
    const serializedFormData = JSON.stringify(formData);
    if (serializedFormData === lastEmittedFormDataRef.current) return;
    lastEmittedFormDataRef.current = serializedFormData;
    onFormDataChange?.(formData);
  }, [formData, isLoadingInitial, isMultiCreateMode, onFormDataChange]);

  const handleSingleRowChange = useCallback((data: CounterFormData) => {
    setFormData(data);
  }, []);

  const handleEquipmentSelectorChange = useCallback((data: CounterFormData) => {
    setEquipmentSelectorData(data);
    setMultiCreateEquipmentId((data.equipment_id as string | number | null) ?? null);
  }, []);

  const handleKindValueChange = useCallback((kind: string, value: string) => {
    setKindValues((prev) => ({ ...prev, [kind]: value }));
  }, []);

  const handleRemoveScanCounter = useCallback(() => {
    setShowScanCounter(false);
    setKindValues((prev) => {
      const next = { ...prev };
      delete next.SP;
      return next;
    });
  }, []);

  // One entry per currently-visible kind that has a value - shared between
  // the low-value-warning check and the actual submit. Scanned pages is
  // excluded unless the user opted into it via "Add Scan counter".
  const getMultiCreateRowData = useCallback(
    () =>
      visibleKindChoices
        .map((choice) =>
          buildDefaultValues({
            equipmentId: multiCreateEquipmentId ?? undefined,
            jobId,
            initialData: { kind: choice.value, value: kindValues[choice.value] },
          })
        )
        .filter(hasFilledCounterRow),
    [visibleKindChoices, jobId, kindValues, multiCreateEquipmentId]
  );

  const performSubmit = async () => {
    if (isMultiCreateMode) {
      const counters = getMultiCreateRowData().map(buildCounterPayload);

      if (counters.length === 0) {
        enqueueSnackbar(t("Please fill at least one counter."), ERROR_SNACKBAR);
        return;
      }

      const result = await executeBulkRequest(() =>
        axios.post(EQUIPMENT_COUNTERS_BULK_ENDPOINT, { counters })
      );

      if (result?.success) {
        enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
        onSuccess?.(result.data);
        return;
      }

      enqueueSnackbar(
        getRequestErrorMessage(
          result?.errorData,
          t("There was an error, please review the form.")
        ),
        ERROR_SNACKBAR
      );
      return;
    }

    const result = id
      ? await update({ id, ...buildCounterPayload(formData) })
      : await create(buildCounterPayload(formData));

    if (result?.success) {
      enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
      onSuccess?.(result.data);
      return;
    }

    const failureMessage =
      (typeof result?.errorData === "string"
        ? result.errorData
        : result?.errorData?.message) ||
      (typeof result?.data === "string" ? result.data : result?.data?.message) ||
      getRequestErrorMessage(error, t("There was an error, please review the form.")) ||
      t("There was an error, please review the form.");

    enqueueSnackbar(failureMessage, ERROR_SNACKBAR);
  };

  const requestSubmit = () => {
    if (canEnterEveryCounterValue) {
      const entries = isMultiCreateMode
        ? getMultiCreateRowData().map((data) => ({
            formData: data,
            lastCounters: multiCreateLastCounters,
          }))
        : [{ formData, lastCounters: singleModeLastCounters }];

      const warnings = collectLowCounterValueWarnings(entries);
      if (warnings.length > 0) {
        setLowValueWarnings(
          warnings.map((warning) => ({
            key: warning.kind,
            label: t(getCounterKindLabel(warning.kind)),
            enteredValue: warning.enteredValue,
            latestValue: warning.latestValue,
            latestDate: warning.latestDate,
          }))
        );
        setLowValueConfirmOpen(true);
        return;
      }
    }

    void performSubmit();
  };

  const handleConfirmLowValueSubmit = () => {
    if (!canEnterEveryCounterValue) {
      setLowValueConfirmOpen(false);
      return;
    }

    setLowValueConfirmOpen(false);
    void performSubmit();
  };

  const lowValueConfirmModal = canEnterEveryCounterValue ? (
    <LowCounterValueConfirmModal
      opened={lowValueConfirmOpen}
      setOpened={setLowValueConfirmOpen}
      warnings={lowValueWarnings}
      onConfirm={handleConfirmLowValueSubmit}
    />
  ) : null;

  if (isLoadingInitial) {
    return <CustomCircularProgress />;
  }

  if (isMultiCreateMode) {
    // Step 1: no equipment known yet - ask for it and hide everything else.
    if (!multiCreateEquipmentId) {
      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <EquipmentCounterRowFields
            formData={equipmentSelectorData}
            onChange={handleEquipmentSelectorChange}
            hiddenFields={["kind", "job", "read_at", "value"]}
            equipmentOptionsOverride={equipmentOptionsOverride}
            wideFields
          />
        </Box>
      );
    }

    // Step 2: equipment known - show one input per counter kind it supports,
    // pre-filled from the latest reading for that kind.
    return (
      <>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Print fontSize="small" color="action" />
            <Typography variant="subtitle1">
              {multiCreateEquipment?.fullName || multiCreateEquipment?.serial || t("Equipment")}
            </Typography>
          </Box>

          {!hidePreviousCountersTable && (
            <PreviousCountersTable equipmentId={multiCreateEquipmentId} />
          )}

          <Typography variant="subtitle2">{t("Counter values needed")}</Typography>

          {visibleKindChoices.map((choice) => {
            const isRemovable = choice.value === "SP";

            return (
              <Box key={choice.value} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  size="small"
                  label={t(choice.label)}
                  value={formatCounterInputValue(kindValues[choice.value] ?? "")}
                  onChange={(event) => {
                    const normalizedValue = normalizeCounterInputValue(event.target.value);
                    if (normalizedValue === null) {
                      return;
                    }
                    handleKindValueChange(choice.value, normalizedValue);
                  }}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  fullWidth
                />
                {isRemovable && (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={handleRemoveScanCounter}
                    aria-label={t("Remove")}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
            );
          })}

          {scanKindChoice && !showScanCounter && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                size="small"
                variant="text"
                startIcon={<Add fontSize="small" />}
                onClick={() => setShowScanCounter(true)}
              >
                {t("Add Scan counter")}
              </Button>
            </Box>
          )}

          {!hideSubmit && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                color="success"
                disabled={bulkLoading}
                onClick={() => requestSubmit()}
              >
                {t("Confirm counter values", { defaultValue: "Confirm" })}
              </Button>
            </Box>
          )}
        </Box>
        {lowValueConfirmModal}
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {!hidePreviousCountersTable && (
          <PreviousCountersTable equipmentId={formData.equipment_id} />
        )}

        <EquipmentCounterRowFields
          formData={formData}
          onChange={handleSingleRowChange}
          hiddenFields={hiddenFields}
          equipmentOptionsOverride={equipmentOptionsOverride}
          fieldErrors={error}
          hideLastValueHint={hideLastValueHint}
          wideFields={wideFields}
        />

        {!hideSubmit && (
          <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
            <Button variant="contained" color="success" disabled={loading} onClick={() => requestSubmit()}>
              {t("Confirm counter values", { defaultValue: "Confirm" })}
            </Button>
          </Box>
        )}
      </Box>
      {lowValueConfirmModal}
    </>
  );
}
